import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

export interface IScoreArcGaugeProps {
  /** The reading, on the scale `min`..`max`. */
  value: number;
  min?: number;
  max?: number;
  /** The hue the swept arc and the needle carry — a `tokens.color` value. */
  color?: string;
  /** Printed beside the reading, e.g. `Low`. */
  statusLabel?: string;
  /** Printed above the reading, e.g. `Financial Stress Score`. */
  label?: string;
  ariaLabel?: string;
  className?: string;
}

/* 3583-10328 is Group 40554, 299.28 x 117.65 - a WIDE box, so the dial sits to the LEFT of its
   reading rather than above it, and the two end labels sit under the dial's own ends. The frame
   draws a needle from the dial's pivot, which is what distinguishes it from the linear stress track
   Section 6 draws for the same metric. PLA-8828. */
const BOX_WIDTH = 118;
const BOX_HEIGHT = 66;
const RADIUS = 48;
const STROKE = 6;
const CENTRE_X = BOX_WIDTH / 2;
const CENTRE_Y = BOX_HEIGHT - 8;
const NEEDLE_LENGTH = RADIUS - 8;
const PIVOT_RADIUS = 4;

const pointOnArc = (fraction: number, radius: number): { x: number; y: number } => {
  const angle = Math.PI * (1 - fraction);

  return { x: CENTRE_X + radius * Math.cos(angle), y: CENTRE_Y - radius * Math.sin(angle) };
};

const arcPath = (fromFraction: number, toFraction: number): string => {
  const from = pointOnArc(fromFraction, RADIUS);
  const to = pointOnArc(toFraction, RADIUS);

  // A half-circle never exceeds 180 degrees, so the large-arc flag stays 0.
  return `M ${from.x} ${from.y} A ${RADIUS} ${RADIUS} 0 0 1 ${to.x} ${to.y}`;
};

const Root = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

const Dial = styled.div`
  flex: 0 0 auto;
`;

const EndLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -4px;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['caption-size']};
  line-height: 1;
`;

const Readout = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
`;

const Label = styled.span`
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['metric-title-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['metric-title-line']};
`;

const Reading = styled.div`
  display: flex;
  align-items: baseline;
  margin-top: ${tokens.space['4']};

  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

/* The gauge reading is the one card value written as a literal rather than through a type role, so
   it takes the same step down the other values took at the token. Scaled, which the 28 was not. */
const Value = styled.span`
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['stat-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: 1;
`;

const Status = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: 1;
`;

/**
 * The needle dial the Customer Profile KPI strip draws for a 0-100 score (`3583-10328`).
 */
const ScoreArcGauge: React.FC<IScoreArcGaugeProps> = ({
  value,
  min = 0,
  max = 100,
  color = tokens.color.positive,
  statusLabel,
  label,
  ariaLabel,
  className
}) => {
  const span = max - min;
  /* A 0-100 score dial has one decimal of room and the wire has no obligation to round: UAT
     answers `financial_stress_signal` 5.31625, which drew all six digits under the label. The DIAL
     still sweeps on the full-precision reading. PLA-8828. */
  const printed = Number(value.toFixed(1));
  // A zero span would divide by zero, and a reading off the scale would sweep past the dial's end.
  const fraction = span > 0 ? Math.min(Math.max((value - min) / span, 0), 1) : 0;
  const needle = pointOnArc(fraction, NEEDLE_LENGTH);

  return (
    <Root className={className} data-testid="score-arc-gauge">
      <Dial>
        <svg
          width={BOX_WIDTH}
          height={BOX_HEIGHT}
          viewBox={`0 0 ${BOX_WIDTH} ${BOX_HEIGHT}`}
          role="img"
          aria-label={ariaLabel ?? `${printed} of ${max}`}
        >
          <path
            d={arcPath(0, 1)}
            fill="none"
            stroke={tokens.color['border-subtle']}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
          {fraction > 0 ? (
            <path
              d={arcPath(0, fraction)}
              fill="none"
              stroke={color}
              strokeWidth={STROKE}
              strokeLinecap="round"
              data-testid="score-arc-gauge-fill"
            />
          ) : null}
          <line
            x1={CENTRE_X}
            y1={CENTRE_Y}
            x2={needle.x}
            y2={needle.y}
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            data-testid="score-arc-gauge-needle"
          />
          <circle cx={CENTRE_X} cy={CENTRE_Y} r={PIVOT_RADIUS} fill={color} />
        </svg>
        <EndLabels aria-hidden>
          <span>{String(min).padStart(2, '0')}</span>
          <span>{max}</span>
        </EndLabels>
      </Dial>
      <Readout>
        {label ? <Label>{label}</Label> : null}
        <Reading>
          <Value data-testid="score-arc-gauge-value">{printed}</Value>
          {statusLabel ? <Status $color={color}>{statusLabel}</Status> : null}
        </Reading>
      </Readout>
    </Root>
  );
};

export default ScoreArcGauge;
